import { useState, useEffect, useCallback, useMemo, createContext, useContext } from "react";
import axios, { API, clearSessionToken } from "@/utils/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize user from localStorage cache to prevent flash of login page
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem('uncloned_user');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  // Sync user to localStorage
  useEffect(() => {
    if (user) {
      localStorage.setItem('uncloned_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('uncloned_user');
    }
  }, [user]);

  const checkAuth = useCallback(async (forceCheck = false) => {
    // CRITICAL: If returning from OAuth callback with session_id, skip the /me check
    // AuthCallback will exchange the session_id and establish the session first
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    // Check for auth error in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('error') === 'auth_failed') {
      toast.error("Authentication failed. Please try again.");
      window.history.replaceState(null, "", window.location.pathname);
      setUser(null);
      localStorage.removeItem('uncloned_user');
      clearSessionToken();
      setLoading(false);
      return;
    }
    
    // OPTIMIZATION: Check if there's a cached user in localStorage
    // If no cached user AND no visible cookie hints, skip the API call entirely (instant login page load)
    // Note: httpOnly cookies are NOT visible to JavaScript, so we can't rely solely on document.cookie
    // EXCEPTION: If forceCheck=true (e.g., after access code redemption), always call the API
    const cachedUser = localStorage.getItem('uncloned_user');
    const hasVisibleCookie = document.cookie.includes('session_token=') || 
                             document.cookie.includes('auth_token=');
    
    // Skip auth check ONLY if there's no cached user AND no visible cookies AND not forced
    // If we have a cached user, we MUST validate the session to prevent stale UI
    if (!forceCheck && !cachedUser && !hasVisibleCookie) {
      // No cached user and no cookies = definitely not logged in
      // Skip the API call to make login page load instantly
      console.debug('No cached user or session hints found, skipping auth check');
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      // Add timeout to prevent hanging forever
      const response = await axios.get(`${API}/auth/me`, {
        timeout: 10000  // 10 second timeout
      });
      setUser(response.data);
      
      // Update user location silently (don't block on this)
      axios.post(`${API}/user/update-location`).catch(() => {});
    } catch (error) {
      // Session invalid, expired, or request timed out
      console.debug('Session validation failed:', error.message);
      setUser(null);
      localStorage.removeItem('uncloned_user');
      // DON'T clear session_token here - it may still be valid for header-based auth
      // The token will be cleared on explicit logout only
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Use Emergent-managed Google Auth - redirect to auth callback to process the session_id
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  // Dispatch event when user logs in (for notification popup to catch)
  useEffect(() => {
    if (user && !loading) {
      // User just logged in - dispatch event for notification system
      window.dispatchEvent(new CustomEvent('user-authenticated', { detail: { user } }));
    }
  }, [user, loading]);

  const logout = useCallback(async () => {
    // Determine redirect based on CURRENT URL, not user data
    // This ensures main app users go to /login even if they have client_id
    const currentPath = window.location.pathname;
    const isOnB2BPortal = currentPath.includes('/portal/');
    
    // Extract clientId from URL if on B2B portal
    let redirectUrl = "/login";
    if (isOnB2BPortal) {
      const match = currentPath.match(/\/portal\/([^\/]+)/);
      if (match && match[1]) {
        redirectUrl = `/portal/${match[1]}/login`;
      }
    }
    
    try {
      // Call backend logout - this clears httpOnly cookies via Set-Cookie header
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout API error:", error);
    }
    
    // Clear local state
    setUser(null);
    localStorage.removeItem('uncloned_user');
    clearSessionToken();
    
    // Small delay to ensure cookies are processed by browser before redirect
    setTimeout(() => {
      window.location.href = redirectUrl;
    }, 100);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({ 
    user, 
    setUser, 
    loading, 
    login, 
    logout, 
    checkAuth 
  }), [user, loading, login, logout, checkAuth]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
